/**
 * Build doughnut slices for patron risk score composition.
 * contribution_i = component_risk_i × (weight_i / 100) — sums to risk_score when components/weights are complete.
 */

const DOMAIN_GROUPS = [
  {
    id: 'compliance',
    label: 'Compliance',
    color: '#0891b2',
    keys: ['kyc_compliance', 'aml_screening', 'case_history', 'smr_history', 'ecdd_history'],
  },
  {
    id: 'gaming',
    label: 'Gaming & activity',
    color: '#0d9488',
    keys: ['gaming_behavior', 'activity', 'traceability_baseline'],
  },
  {
    id: 'demographics',
    label: 'Demographics',
    color: '#ea580c',
    keys: ['age', 'gender', 'nationality', 'occupation_risk', 'membership_duration'],
  },
  {
    id: 'profile',
    label: 'Profile',
    color: '#9333ea',
    keys: ['confidence_level', 'profile_age'],
  },
]

const COMPONENT_TO_DOMAIN = {}
for (const g of DOMAIN_GROUPS) {
  for (const k of g.keys) COMPONENT_TO_DOMAIN[k] = g.id
}

/** Map display component names from risk_factors to domain ids */
const FACTOR_COMPONENT_TO_DOMAIN = {
  'KYC Compliance': 'compliance',
  'KYC at payout': 'compliance',
  'AML Screening': 'compliance',
  'Case History': 'compliance',
  'SMR History': 'compliance',
  'ECDD History': 'compliance',
  Jurisdiction: 'demographics',
  Nationality: 'demographics',
  'Gaming Behavior': 'gaming',
  'Activity Level': 'gaming',
  Traceability: 'gaming',
  Age: 'demographics',
  Gender: 'demographics',
  'Membership Duration': 'demographics',
  'Occupation Risk': 'demographics',
  'ID Confidence': 'profile',
  'Profile Age': 'profile',
  System: null,
}

const DEFAULT_FACTOR_PATTERNS = [
  /not available/i,
  /not recorded/i,
  /not provided/i,
  /unknown/i,
  /baseline/i,
  /limited identity/i,
  /traceability/i,
  /no verified/i,
  /never screened/i,
  /risk model/i,
]

function round2(n) {
  return Math.round(Number(n) * 100) / 100
}

function componentContributions(components, weights) {
  const byKey = {}
  if (!components || !weights) return byKey

  for (const [key, weight] of Object.entries(weights)) {
    const comp = components[key]
    if (!comp || weight == null) continue
    const componentRisk = Number(comp.score)
    if (!Number.isFinite(componentRisk)) continue
    const w = Number(weight) || 0
    byKey[key] = round2((componentRisk * w) / 100)
  }
  return byKey
}

function groupContributions(byKey) {
  const domainTotals = Object.fromEntries(DOMAIN_GROUPS.map((g) => [g.id, 0]))
  for (const [key, pts] of Object.entries(byKey)) {
    const domainId = COMPONENT_TO_DOMAIN[key]
    if (domainId && pts > 0) domainTotals[domainId] = round2(domainTotals[domainId] + pts)
  }
  return domainTotals
}

function domainSlices(domainTotals, riskScore) {
  const slices = DOMAIN_GROUPS
    .map((g) => ({
      id: g.id,
      name: g.label,
      value: round2(domainTotals[g.id] || 0),
      color: g.color,
      source: 'components',
    }))
    .filter((s) => s.value > 0)

  const sliceSum = round2(slices.reduce((a, s) => a + s.value, 0))
  const target = round2(Number(riskScore) || 0)
  const remainder = round2(Math.max(0, target - sliceSum))

  if (remainder > 0.05) {
    slices.push({
      id: 'baseline',
      name: 'Baseline / other',
      value: remainder,
      color: '#94a3b8',
      source: 'components',
      isBaseline: true,
    })
  }

  return { slices, sliceSum: round2(sliceSum + remainder), target }
}

function buildFromRiskFactors(riskFactors, riskScore) {
  const domainTotals = Object.fromEntries(DOMAIN_GROUPS.map((g) => [g.id, 0]))
  let hasDefault = false

  for (const f of riskFactors || []) {
    if (f?.factor === 'Risk model' && (f.score || 0) === 0) continue
    const pts = Number(f?.score) || 0
    if (pts <= 0) continue
    const comp = f?.component || ''
    const domainId = FACTOR_COMPONENT_TO_DOMAIN[comp] || 'compliance'
    if (!domainId) continue
    domainTotals[domainId] = round2(domainTotals[domainId] + pts)
    const text = `${f?.factor || ''} ${f?.detail || ''}`
    if (DEFAULT_FACTOR_PATTERNS.some((re) => re.test(text))) hasDefault = true
  }

  const { slices, sliceSum, target } = domainSlices(domainTotals, riskScore)
  return {
    slices,
    sliceSum,
    target,
    source: 'risk_factors',
    hasDefaultEstimate: hasDefault,
  }
}

/**
 * @param {{ components?: object, weights?: object, riskScore?: number, riskFactors?: array }} input
 */
export function buildPatronRiskDoughnutData(input = {}) {
  const riskScore = Number(input.riskScore)
  const { components, weights, riskFactors } = input

  if (components && weights && Object.keys(weights).length > 0) {
    const byKey = componentContributions(components, weights)
    const domainTotals = groupContributions(byKey)
    const result = domainSlices(domainTotals, riskScore)
    return {
      ...result,
      source: 'components',
      hasDefaultEstimate: false,
      byKey,
    }
  }

  if (riskFactors?.length) {
    return buildFromRiskFactors(riskFactors, riskScore)
  }

  return {
    slices: [],
    sliceSum: 0,
    target: Number.isFinite(riskScore) ? riskScore : 0,
    source: 'none',
    hasDefaultEstimate: false,
  }
}

export function primaryRiskDriver(riskFactors) {
  const factors = (riskFactors || []).filter(
    (f) => f?.factor !== 'Risk model' && (Number(f?.score) || 0) > 0
  )
  if (!factors.length) return null
  const top = [...factors].sort((a, b) => (Number(b.score) || 0) - (Number(a.score) || 0))[0]
  return {
    name: String(top.factor || '').replace(/_/g, ' '),
    detail: top.detail || top.reason || null,
    score: top.score,
  }
}

export function filterFactorsByDomain(riskFactors, domainId) {
  if (!domainId || !Array.isArray(riskFactors)) return riskFactors || []
  return riskFactors.filter((f) => {
    const comp = f?.component || ''
    const d = FACTOR_COMPONENT_TO_DOMAIN[comp]
    if (domainId === 'baseline') return false
    return d === domainId
  })
}

export { DOMAIN_GROUPS }
